import { useState, useEffect, useRef, useCallback } from "react";
import { invoke } from "../lib/bridge";
import { QRCodeSVG } from "qrcode.react";
import { generateAndPushSnapshots } from "./InviteView";
import { Spinner } from "./ui/Spinner";

interface LinkData {
  payload: string;
  code: string;
  expiresAt: string;
}

interface PendingRequest {
  id: number;
  memberName: string;
  publicKey: string;
  deviceLink: boolean;
  createdAt: string;
}

interface LinkDeviceViewProps {
  onBack: () => void;
}

export function LinkDeviceView({ onBack }: LinkDeviceViewProps) {
  const [link, setLink] = useState<LinkData | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [linkedCount, setLinkedCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const handledRef = useRef<Set<number>>(new Set());

  const fetchCode = useCallback(() => {
    setError("");
    invoke<LinkData>("get_device_link_code")
      .then((data) => {
        setLink(data);
        // Push snapshots so the new device receives all docs on approval — non-blocking.
        generateAndPushSnapshots(data.code);
      })
      .catch((e) => {
        console.error("get_device_link_code failed:", e);
        setError(String(e));
      });
  }, []);

  useEffect(() => {
    fetchCode();
  }, [fetchCode]);

  // Refresh the code when it expires.
  useEffect(() => {
    if (!link) return;

    const tick = () => {
      const expiresAtMs = new Date(link.expiresAt).getTime();
      const secs = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0) fetchCode();
    };

    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [link, fetchCode]);

  // Auto-approve device-link requests — it's you, on another device.
  useEffect(() => {
    const poll = async () => {
      try {
        const reqs = await invoke<PendingRequest[]>("list_join_requests");
        for (const r of reqs) {
          if (!r.deviceLink || handledRef.current.has(r.id)) continue;
          handledRef.current.add(r.id);
          try {
            await invoke("approve_join_request", { requestId: r.id, joinerPublicKeyB64: r.publicKey });
            setLinkedCount((n) => n + 1);
          } catch (e) {
            console.error("Auto-approve failed:", e);
            handledRef.current.delete(r.id); // allow retry on the next poll
          }
        }
      } catch {
        // offline / not authenticated
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareLink = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Link my device", url: link.payload });
      } catch {
        // share dismissed — nothing to do
      }
      return;
    }
    await navigator.clipboard.writeText(link.payload);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="container" style={{ padding: "16px" }}>
      <div className="header-row">
        <h1 className="header-title">Link a device</h1>
        <button onClick={onBack} className="btn-close">&times;</button>
      </div>

      {error ? (
        <p style={{ color: "#e53e3e" }}>{error}</p>
      ) : !link ? (
        <Spinner />
      ) : (
        <>
          <p className="invite-subtitle">
            On your other device, open Nuthatch Family and scan this code (or enter it). It joins as another
            device of yours — no new family member is created.
          </p>

          {linkedCount > 0 && (
            <p className="invite-linked-ok">
              ✓ {linkedCount === 1 ? "A device was" : `${linkedCount} devices were`} linked. You can
              link another or close this.
            </p>
          )}

          <div className="invite-qr">
            <QRCodeSVG value={link.payload} size={200} />
          </div>

          <div className="invite-divider">
            <span>or enter this code</span>
          </div>

          <div className="invite-code-box">
            <code className="invite-short-code">{link.code}</code>
            <span className="invite-timer">
              Expires in {mins}:{secs.toString().padStart(2, "0")}
            </span>
            <button onClick={handleCopy} className="btn-primary btn-small">
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>

          <div className="invite-divider">
            <span>or share this link</span>
          </div>

          <div className="invite-link-box">
            <span className="invite-link-text">{link.payload}</span>
            <button onClick={handleShareLink} className="btn-primary btn-small">
              {linkCopied ? "Copied!" : canShare ? "Share" : "Copy link"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
